import {
  RFAS_SET,
  RFA_LIST_LOADING_SET,
  RFA_SET,
  RFA_LOG_PDF_SET,
  RFA_DELETE,
  RFA_NOTES_SET,
  RFA_NOTE_ADD,
  RFA_FORM_SET,
  RFA_FORM_RESET,
  RFA_DATA_CHANGED_SET,
  RFA_SIGNATURE_PREVIEW_PAGES_LOAD,
  RFA_SIGNATURE_ADD,
  RFA_SIGNATURE_UPDATE,
  RFA_SIGNATURE_REMOVE,
  RFA_SIGNATURE_DUPLICATE,
  RFA_SIGNATURE_SET_ACTIVE,
  RFA_SIGNATURE_CLEAR_PAGE,
  RFA_SIGNATURE_RESET,
  RFA_SIGNATURE_SET_SUBMITTING,
  RFA_SIGNING_ZONES_PREVIEW_PAGES_LOAD,
  RFA_SIGNING_ZONE_ADD,
  RFA_SIGNING_ZONE_UPDATE,
  RFA_SIGNING_ZONE_REMOVE,
  RFA_SIGNING_ZONE_DUPLICATE,
  RFA_SIGNING_ZONE_SET_ACTIVE,
  RFA_SIGNING_ZONES_RESET,
  RFA_SIGNING_ZONES_SET_LOADING,
  RFA_SIGNING_ZONES_SET_SAVING,
} from 'components/admin/projects/details/project-rfa-log/store/action-types';
import {
  DEFAULT_STATUS_OPTION,
  DEFAULT_DUE_OPTION,
  DEFAULT_PRE_OPTION,
} from 'domain/rfa';

const initialState = {
  rfas: [],
  total: 0,
  rfaList: {
    isLoading: false,
  },
  currentRfa: null,
  logPdf: null,
  logPdfType: 'url',
  dataChanged: false,
  filters: {
    due: DEFAULT_DUE_OPTION,
    page: 1,
    status: DEFAULT_STATUS_OPTION,
    search: '',
    pageSize: 25,
    pre: DEFAULT_PRE_OPTION,
    number_order: 'all',
    company_number_order: 'all',
    title_order: 'all',
    submitted_order: 'all',
    reviewed_order: 'all',
    resolved_order: 'all',
    accelerate: false,
    for_record: false,
    by_request_type: null,
    by_submitted_user: [],
    by_response_user: [],
    by_due_to: null,
    by_due_from: null,
    by_submitted_date_to: null,
    by_submitted_date_from: null,
  },
  rfaNotes: [],
  form: {
    status: null,
    formatted_status: null,
    request_type: null,
    request_title: '',
    request_description: '',
    due_date: null,
    csi_code_id: null,
    csi_code_label: '00',
    submitted_by: null,
    submitted_at: null,
    assigned_to: null,
    signatory: null, // Keep for backward compatibility, but prefer signatories
    signatories: [], // Array of {value, label} for multiple signatories
    ccs: [],
    // External CC / Assign-To (increment 3): array of { id, email, name }
    // for outsiders with no 360PLUS account, kept separate from `ccs`
    // (which holds directory User [id, label] pairs) -- see
    // docs/external-cc-implementation-plan-2026-09-04.md.
    external_ccs: [],
    addl_recipients: [],
    suppress_assignee_notifications: false,
    request_documents: [],
    request_images: [],
    deleted_request_documents: [],
    deleted_request_images: [],

    response_code: null,
    formatted_response_code: null,
    response_description: '',
    responded_by: null,
    responded_at: null,
    responded_at_date: null,
    responded_at_time: null,
    cost: null,
    accelerated_by: null,
    accelerated_at: null,
    accelerated: false,
    response_method: null,
    formatted_response_method: null,
    response_method_description: '',
    response_documents: [],
    response_images: [],
    deleted_response_documents: [],
    deleted_response_images: [],

    review_description: null,
    reviewed_by: null,
    reviewed_at: null,
    review_documents: [],
    review_images: [],
    deleted_review_documents: [],
    deleted_review_images: [],

    errors: null,
  },
  signatureEditor: {
    previewPages: [], // [{pageIndex, imageUrl, imageWidthPx, imageHeightPx, pageWidthPt?, pageHeightPt?}]
    signatures: [], // [{id, pageIndex, src (dataURL), nx, ny, nw, nh, rotation?}]
    activeSignatureId: null,
    isLoadingPages: false,
    isSubmitting: false,
  },
  signingZonesEditor: {
    previewPages: [], // [{pageIndex, imageUrl, imageWidthPx, imageHeightPx, pageWidthPt?, pageHeightPt?}]
    zones: [], // [{id, pageIndex, nx, ny, nw, nh, rotation?, assigned_user_id?}]
    activeZoneId: null,
    isLoadingPages: false,
    isSaving: false,
  },
};

export default function projectRfaLogDataReducer(state = initialState, action) {
  switch (action.type) {
    case RFAS_SET:
      return {
        ...state,
        ...action.payload,
        rfaList: {
          ...state.rfaList,
          isLoading: false,
        },
      };

    case RFA_LIST_LOADING_SET:
      return {
        ...state,
        rfaList: {
          ...state.rfaList,
          isLoading: Boolean(action.payload),
        },
      };

    case RFA_LOG_PDF_SET:
      return {
        ...state,
        logPdf: action.payload.logPdf,
        logPdfType: action.payload.logPdfType,
      };

    case RFA_DATA_CHANGED_SET:
      return { ...state, dataChanged: action.payload };

    case RFA_SET:
      return {
        ...state,
        currentRfa: action.payload.currentRfa,
      };

    case RFA_DELETE:
      return {
        ...state,
        rfas: state.rfas.filter((rfa) => rfa.id !== action.payload.rfaId),
      };

    case RFA_FORM_SET:
      return {
        ...state,
        form: { ...state.form, ...action.payload.values },
      };

    case RFA_FORM_RESET:
      return {
        ...state,
        form: { ...initialState.form, ...action.payload.values },
      };

    case RFA_NOTES_SET:
      return { ...state, rfaNotes: action.payload };

    case RFA_NOTE_ADD:
      return {
        ...state,
        rfaNotes: [...state.rfaNotes, action.payload],
      };

    case RFA_SIGNATURE_PREVIEW_PAGES_LOAD:
      return {
        ...state,
        signatureEditor: {
          ...state.signatureEditor,
          previewPages: action.payload.pages || [],
          isLoadingPages:
            action.payload.isLoadingPages !== undefined
              ? action.payload.isLoadingPages
              : false,
        },
      };

    case RFA_SIGNATURE_ADD:
      return {
        ...state,
        signatureEditor: {
          ...state.signatureEditor,
          signatures: [
            ...state.signatureEditor.signatures,
            action.payload.signature,
          ],
          activeSignatureId: action.payload.signature.id,
        },
      };

    case RFA_SIGNATURE_UPDATE:
      return {
        ...state,
        signatureEditor: {
          ...state.signatureEditor,
          signatures: state.signatureEditor.signatures.map((sig) =>
            sig.id === action.payload.signatureId
              ? { ...sig, ...action.payload.updates }
              : sig
          ),
        },
      };

    case RFA_SIGNATURE_REMOVE:
      return {
        ...state,
        signatureEditor: {
          ...state.signatureEditor,
          signatures: state.signatureEditor.signatures.filter(
            (sig) => sig.id !== action.payload.signatureId
          ),
          activeSignatureId:
            state.signatureEditor.activeSignatureId ===
            action.payload.signatureId
              ? null
              : state.signatureEditor.activeSignatureId,
        },
      };

    case RFA_SIGNATURE_DUPLICATE:
      const sourceSig = state.signatureEditor.signatures.find(
        (sig) => sig.id === action.payload.signatureId
      );
      if (!sourceSig) return state;
      const newId = `sig_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const duplicatedSig = {
        ...sourceSig,
        id: newId,
        nx: sourceSig.nx + 0.05, // Offset slightly
        ny: sourceSig.ny + 0.05,
      };
      return {
        ...state,
        signatureEditor: {
          ...state.signatureEditor,
          signatures: [...state.signatureEditor.signatures, duplicatedSig],
          activeSignatureId: newId,
        },
      };

    case RFA_SIGNATURE_SET_ACTIVE:
      return {
        ...state,
        signatureEditor: {
          ...state.signatureEditor,
          activeSignatureId: action.payload.signatureId,
        },
      };

    case RFA_SIGNATURE_CLEAR_PAGE:
      return {
        ...state,
        signatureEditor: {
          ...state.signatureEditor,
          signatures: state.signatureEditor.signatures.filter(
            (sig) => sig.pageIndex !== action.payload.pageIndex
          ),
          activeSignatureId: null,
        },
      };

    case RFA_SIGNATURE_RESET:
      return {
        ...state,
        signatureEditor: {
          previewPages: [],
          signatures: [],
          activeSignatureId: null,
          isLoadingPages: false,
          isSubmitting: false,
        },
      };

    case RFA_SIGNATURE_SET_SUBMITTING:
      return {
        ...state,
        signatureEditor: {
          ...state.signatureEditor,
          isSubmitting: action.payload.isSubmitting,
        },
      };

    case RFA_SIGNING_ZONES_PREVIEW_PAGES_LOAD:
      return {
        ...state,
        signingZonesEditor: {
          ...state.signingZonesEditor,
          previewPages: action.payload.pages || [],
          isLoadingPages:
            action.payload.isLoadingPages !== undefined
              ? action.payload.isLoadingPages
              : false,
        },
      };

    case RFA_SIGNING_ZONE_ADD:
      return {
        ...state,
        signingZonesEditor: {
          ...state.signingZonesEditor,
          zones: [...state.signingZonesEditor.zones, action.payload.zone],
          activeZoneId: action.payload.zone.id,
        },
      };

    case RFA_SIGNING_ZONE_UPDATE:
      return {
        ...state,
        signingZonesEditor: {
          ...state.signingZonesEditor,
          zones: state.signingZonesEditor.zones.map((zone) =>
            zone.id === action.payload.zoneId
              ? { ...zone, ...action.payload.updates }
              : zone
          ),
        },
      };

    case RFA_SIGNING_ZONE_REMOVE:
      return {
        ...state,
        signingZonesEditor: {
          ...state.signingZonesEditor,
          zones: state.signingZonesEditor.zones.filter(
            (zone) => zone.id !== action.payload.zoneId
          ),
          activeZoneId:
            state.signingZonesEditor.activeZoneId === action.payload.zoneId
              ? null
              : state.signingZonesEditor.activeZoneId,
        },
      };

    case RFA_SIGNING_ZONE_DUPLICATE:
      const sourceZone = state.signingZonesEditor.zones.find(
        (zone) => zone.id === action.payload.zoneId
      );
      if (!sourceZone) return state;
      const newZoneId = `zone_${Date.now()}_${Math.random()
        .toString(36)
        .substr(2, 9)}`;
      const duplicatedZone = {
        ...sourceZone,
        id: newZoneId,
        nx: sourceZone.nx + 0.05, // Offset slightly
        ny: sourceZone.ny + 0.05,
      };
      return {
        ...state,
        signingZonesEditor: {
          ...state.signingZonesEditor,
          zones: [...state.signingZonesEditor.zones, duplicatedZone],
          activeZoneId: newZoneId,
        },
      };

    case RFA_SIGNING_ZONE_SET_ACTIVE:
      return {
        ...state,
        signingZonesEditor: {
          ...state.signingZonesEditor,
          activeZoneId: action.payload.zoneId,
        },
      };

    case RFA_SIGNING_ZONES_RESET:
      return {
        ...state,
        signingZonesEditor: {
          previewPages: [],
          zones: [],
          activeZoneId: null,
          isLoadingPages: false,
          isSaving: false,
        },
      };

    case RFA_SIGNING_ZONES_SET_LOADING:
      return {
        ...state,
        signingZonesEditor: {
          ...state.signingZonesEditor,
          isLoadingPages: action.payload.isLoading,
        },
      };

    case RFA_SIGNING_ZONES_SET_SAVING:
      return {
        ...state,
        signingZonesEditor: {
          ...state.signingZonesEditor,
          isSaving: action.payload.isSaving,
        },
      };

    default:
      return state;
  }
}
